import type { ReactNode } from 'react';
import { createFileRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { ApiError } from '../lib/api.js';
import { ProtectedRoute } from '../components/auth/index.js';
import { ProjectDetailTabs, BACKLOG_BASE } from '../components/features/project/index.js';
import { ProjectNameEditor } from '../components/features/project/ProjectNameEditor.js';
import { useProjectDetail, useIntegratedProductDetail } from '../hooks/useProjects.js';

/**
 * Gen1統合プロダクトキーか（"VIRTUAL:" で始まらない実在キーか）を判定する。
 * テストから直接検証できるよう named export にしている。
 * @graph-connects none
 */
export function isRealIntegratedProductKey(key: string | null): key is string {
  return key !== null && !key.startsWith('VIRTUAL:');
}

/**
 * 要件定義タブの遷移先キーを決める。Gen2 がチャンク分割済み（実在の integratedProductKey を持つ）
 * なら、要件定義は分割前 = Gen2 発行時点の Gen1 キーの下に書かれているため、その Gen1 キーへ飛ばす。
 * それ以外（Gen1 ページ自身・チャンク未分割の Gen2）は自分自身の pjKey を使う。
 * テストから直接検証できるよう named export にしている。
 * @graph-connects none
 */
export function resolveSpecPjKey(
  pjKey: string,
  integratedProductKey: string | null | undefined,
): string {
  const key = integratedProductKey ?? null;
  return isRealIntegratedProductKey(key) ? key : pjKey;
}

/**
 * ヘッダに案件名を出せないと**確定した**かを判定する。
 *
 * 要件定義フェーズの母票は種別が「アサイン前」のままのものが大半で、`generation` が NULL になる
 * （generation は種別 ID 直書きの CASE で決まる）。そのため project_detail_table（generation=2）にも
 * Gen1 フォールバック（generation=1）にも載らず、案件名が引けない。このとき案件名の位置に案件キーを
 * 代入すると、見出しと直下の Backlog リンクが同じキーの二重表示になるため、呼び出し側はキーを
 * Backlog リンクとして 1 回だけ出す。
 *
 * **取得中は false を返す**。名前が出る可能性が残っている間に退化表示へ落とすと、ヘッダが
 * 「キーだけ」→「案件名 + キー」と 2 回組み替わる。
 * テストから直接検証できるよう named export にしている。
 * @graph-connects none
 */
export function isSummaryUnavailable({
  summary,
  isProjectPending,
  isIntegratedProductPending,
}: {
  summary: string | undefined;
  isProjectPending: boolean;
  /** Gen1 フォールバックの取得中フラグ。フォールバックを張っていないときは false を渡す。 */
  isIntegratedProductPending: boolean;
}): boolean {
  if (summary !== undefined) return false;
  return !isProjectPending && !isIntegratedProductPending;
}

/**
 * タブごとの器の幅を決める。基本設計タブは子票を 8 列の表で組み立てるため、
 * 他タブと同じ `max-w-6xl`（1152px）だと列が詰まって読めない。このタブだけ広く受ける。
 * テストから直接検証できるよう named export にしている。
 * @graph-connects none
 */
export function projectDetailContainerClass(pathname: string): string {
  const base = 'mx-auto w-full space-y-4 py-6';
  return pathname.endsWith('/basic-design')
    ? `${base} max-w-[1680px] px-6`
    : `${base} max-w-6xl px-4`;
}

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件詳細ページのレイアウト。ヘッダ（案件名・Backlogリンク）+ セクション内タブ（ダッシュボード/要件定義/打ち合わせ）を描画し、配下のタブページを Outlet でラップする。pjKey が Gen2 として404の場合、Gen1統合プロダクトとしてフォールバック取得しヘッダを描画する。どちらにも無い母票（generation が NULL の「アサイン前」種別）では案件名が引けないため、案件キーを Backlog リンクとして見出しに 1 回だけ出す。アクセス可否は要件定義タブと同一（認証済みなら全員）
 * @graph-connects ProtectedRoute [calls] ページアクセス判定でラップ
 * @graph-connects ProjectDetailTabs [calls] セクション内タブナビ描画
 * @graph-connects useProjectDetail [calls] Gen2案件の取得（ヘッダに出す案件名用）
 * @graph-connects useIntegratedProductDetail [calls] useProjectDetailが404のときのGen1統合プロダクトのフォールバック取得
 * @graph-connects isSummaryUnavailable [calls] 案件名が引けないと確定したかの判定（ヘッダの退化表示）
 * @graph-connects projectDetailContainerClass [calls] タブごとの器の幅の決定
 * @graph-connects ProjectNameEditor [calls] 案件名の見出しとその場での変更
 */
export const Route = createFileRoute('/projects/$pjKey')({
  component: ProjectDetailLayout,
});

/** @graph-connects none */
function ProjectDetailLayout(): ReactNode {
  const { pjKey } = Route.useParams();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const projectQuery = useProjectDetail(pjKey);
  const isGen2NotFound =
    projectQuery.error instanceof ApiError && projectQuery.error.status === 404;
  const integratedProductQuery = useIntegratedProductDetail(pjKey, { enabled: isGen2NotFound });

  const project = projectQuery.data;
  const integratedProduct = isGen2NotFound ? integratedProductQuery.data : undefined;
  const summary = project?.summary ?? integratedProduct?.summary;
  // Gen1 フォールバックは isGen2NotFound のときだけ有効なので、無効（= status が pending のまま）の
  // 取得中フラグを拾わないよう isGen2NotFound で絞る。
  const summaryUnavailable = isSummaryUnavailable({
    summary,
    isProjectPending: projectQuery.isPending,
    isIntegratedProductPending: isGen2NotFound && integratedProductQuery.isPending,
  });
  const specPjKey = resolveSpecPjKey(pjKey, project?.integratedProductKey);

  return (
    <ProtectedRoute pageKey="specRequirements">
      <div className={projectDetailContainerClass(pathname)}>
        <header>
          {summaryUnavailable ? (
            // 案件名が引けない母票。キーを見出しの位置に 1 回だけ出す（Backlog リンクは残す）。
            <h1 className="text-xl font-bold">
              <a
                href={`${BACKLOG_BASE}${pjKey}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--color-accent-primary)] hover:underline"
              >
                {pjKey}
              </a>
            </h1>
          ) : (
            <>
              {summary === undefined ? (
                <h1 className="text-xl font-bold text-[var(--color-text-primary)]">{pjKey}</h1>
              ) : (
                <ProjectNameEditor pjKey={pjKey} summary={summary} />
              )}
              <a
                href={`${BACKLOG_BASE}${pjKey}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[var(--color-accent-primary)] hover:underline"
              >
                {pjKey}
              </a>
            </>
          )}
        </header>
        <ProjectDetailTabs pjKey={pjKey} specPjKey={specPjKey} />
        <Outlet />
      </div>
    </ProtectedRoute>
  );
}
